import {Inject, Injectable, NgZone, OnDestroy, PLATFORM_ID} from '@angular/core';
import {isPlatformBrowser} from '@angular/common';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {BehaviorSubject, firstValueFrom, Subject, Subscription} from 'rxjs';
import {Centrifuge, Subscription as ChannelSubscription} from 'centrifuge';
import {environment} from '../../../environments/environment';
import {TokenDataProviderService} from './token-data-provider.service';
import {NetworkStatusService} from './network-status.service';

export type PresenceState = 'online' | 'offline' | 'unknown';
export interface RealtimeGrant {channel: string; token: string; expiresAt: number;}
type Resource = {type: 'chat' | 'comments' | 'online' | 'userPresence' | 'health'; id?: string | number};
type Entry = {resource: Resource; refs: number; subscription?: ChannelSubscription; ready?: Promise<void>};
export interface OnlineSnapshot {records: any[]; total: number; version: number;}

@Injectable({providedIn: 'root'})
export class EphemeralRealtimeService implements OnDestroy {
  private readonly lifecycle = new Subscription();
  private readonly visibilityChanged = () => { if (document.visibilityState === 'visible') this.resume(); };
  readonly presence$ = new BehaviorSubject<Record<number, PresenceState>>({});
  readonly available$ = new BehaviorSubject(false);
  readonly rosterChanges$ = new Subject<any>();
  readonly synchronized$ = new Subject<void>();
  private client?: Centrifuge;
  private initialized = false;
  private userId: number | null = null;
  private generation = 0;
  private syncEpoch = 0;
  private entries = new Map<string, Entry>();
  private grants = new Map<string, RealtimeGrant>();
  private pendingGrants = new Map<string, {resource: Resource; resolve: (grant: RealtimeGrant) => void; reject: (error: any) => void}[]>();
  private grantTimer?: ReturnType<typeof setTimeout>;
  private startTimer?: ReturnType<typeof setTimeout>;
  private healthTimer?: ReturnType<typeof setTimeout>;
  private renewalTimer?: ReturnType<typeof setTimeout>;
  private sessionToken?: {token: string; expiresAt: number};
  private versions = new Map<number, number>();
  private typing = new Map<string, Map<string, {name: string; until: number}>>();
  private typingSubjects = new Map<string, BehaviorSubject<string[]>>();
  private lastTyping = new Map<string, number>();
  private typingTimer?: ReturnType<typeof setInterval>;
  private headers = new HttpHeaders({'x-cache-bypass': 'true'});

  constructor(
    private readonly http: HttpClient,
    private readonly tokens: TokenDataProviderService,
    private readonly network: NetworkStatusService,
    private readonly zone: NgZone,
    @Inject(PLATFORM_ID) private readonly platformId: object,
  ) {}

  initialize(): void {
    if (this.initialized || !isPlatformBrowser(this.platformId)) return;
    this.initialized = true;
    document.addEventListener('visibilitychange', this.visibilityChanged);
    this.lifecycle.add(this.tokens.user$.subscribe(user => {
      const id = user?.id || null;
      if (id === this.userId) return;
      this.reset(); this.userId = id;
      if (id) void this.start(this.generation);
    }));
    this.lifecycle.add(this.network.status$.subscribe(state => {
      if (!this.userId) return;
      if (!state.connected) { this.client?.disconnect(); this.unavailable(); }
      else if (this.client) this.client.connect();
      else void this.start(this.generation);
    }));
    this.zone.runOutsideAngular(() => { this.typingTimer = setInterval(() => this.expireTyping(), 1000); });
  }

  resume(): void {
    if (!isPlatformBrowser(this.platformId) || !this.userId || this.network.isOffline()) return;
    this.unavailable();
    if (this.client?.state === 'connected') {
      this.synchronized$.next(); void this.resyncUsers();
    } else if (this.client) this.client.connect();
    else void this.start(this.generation);
  }

  private async start(generation: number): Promise<void> {
    if (this.client || generation !== this.generation || !this.userId || this.network.isOffline()) return;
    // Reserve the attempt so a simultaneous network notification cannot start a second connection.
    if (this.startTimer) return;
    const marker = setTimeout(() => {}, 2147483647);
    this.startTimer = marker;
    try {
      const session: any = await firstValueFrom(this.http.post(environment.apiUrl + '/realtime/session', {}, {headers: this.headers}));
      if (generation !== this.generation || !session.enabled) return;
      this.sessionToken = session;
      const client = new Centrifuge(session.websocketUrl, {
        token: session.token,
        getToken: async () => {
          if (generation !== this.generation) throw new Error('Realtime session ended');
          if (this.sessionToken && this.sessionToken.expiresAt * 1000 > Date.now() + 30000) return this.sessionToken.token;
          const value: any = await firstValueFrom(this.http.post(environment.apiUrl + '/realtime/session', {}, {headers: this.headers}));
          if (generation !== this.generation) throw new Error('Realtime session ended');
          if (!value.enabled) { this.reset(); throw new Error('Realtime disabled'); }
          this.sessionToken = value; this.scheduleRenewal(generation, value.expiresAt);
          this.updatePresenceGrant(value.presence);
          return value.token;
        },
        minReconnectDelay: 1000, maxReconnectDelay: 30000,
      });
      this.client = client;
      client.on('connected', () => this.zone.run(() => {
        if (generation !== this.generation || this.client !== client) return;
        this.synchronized$.next();
        void this.resyncUsers();
      }));
      const unavailable = () => {
        if (generation === this.generation && this.client === client) this.zone.run(() => this.unavailable());
      };
      client.on('connecting', unavailable);
      client.on('disconnected', unavailable);
      this.updatePresenceGrant(session.presence);
      this.retain('health:presence', {type: 'health'});
      for (const [channel, entry] of this.entries) this.bind(channel, entry);
      client.connect();
      this.scheduleRenewal(generation, session.expiresAt);
    } catch {
      if (generation === this.generation && this.userId) {
        clearTimeout(marker);
        this.startTimer = setTimeout(() => { this.startTimer = undefined; void this.start(generation); }, 10000 + Math.random() * 10000);
        return;
      }
    } finally {
      // An old account request must not clear the next account's in-flight attempt.
      clearTimeout(marker);
      if (this.startTimer === marker) this.startTimer = undefined;
    }
  }

  private scheduleRenewal(generation: number, expiresAt: number): void {
    clearTimeout(this.renewalTimer);
    // One batched refresh per tab, spread over a 30 second window before expiry.
    this.renewalTimer = setTimeout(() => void this.renew(generation),
      Math.max(1000, expiresAt * 1000 - Date.now() - 60000 - Math.random() * 30000));
  }

  private async renew(generation: number): Promise<void> {
    if (generation !== this.generation || !this.userId) return;
    try {
      const session: any = await firstValueFrom(this.http.post(environment.apiUrl + '/realtime/session', {}, {headers: this.headers}));
      if (generation !== this.generation) return;
      if (!session.enabled) { this.reset(); return; }
      this.sessionToken = session; this.updatePresenceGrant(session.presence);
      this.scheduleRenewal(generation, session.expiresAt);
      const resources = [...this.entries].filter(([channel]) => channel !== 'presence:global').map(([, entry]) => entry.resource);
      if (!resources.length) return;
      const grants = await this.fetchGrants(resources);
      if (generation !== this.generation) return;
      for (const grant of grants) {
        if (this.entries.has(grant.channel)) this.grants.set(grant.channel, grant);
      }
    } catch {
      if (generation === this.generation) {
        clearTimeout(this.renewalTimer);
        this.renewalTimer = setTimeout(() => void this.renew(generation), 10000 + Math.random() * 10000);
      }
    }
  }

  private updatePresenceGrant(grant: RealtimeGrant | null): void {
    const channel = 'presence:global';
    if (!grant) {
      const previous = this.entries.get(channel);
      if (previous) { this.remove(channel); this.entries.delete(channel); }
      return;
    }
    this.grants.set(channel, grant);
    if (!this.entries.has(channel)) this.retain(channel, {type: 'health'});
  }

  watchUsers(ids: number[]): () => void {
    const unique = [...new Set(ids.filter(id => Number.isInteger(id) && id > 0))].slice(0, 50);
    const accepted: number[] = [];
    for (const id of unique) {
      if (!this.entries.has('status:' + id) && this.watchedIds().length >= 50) continue;
      this.retain('status:' + id, {type: 'userPresence', id}); accepted.push(id);
    }
    return () => accepted.forEach(id => this.release('status:' + id));
  }

  private watchedIds(): number[] {
    return [...this.entries.keys()].filter(key => key.startsWith('status:')).map(key => Number(key.slice(7)));
  }

  private retain(channel: string, resource: Resource): Entry {
    let entry = this.entries.get(channel);
    if (entry) { entry.refs++; return entry; }
    entry = {resource, refs: 1}; this.entries.set(channel, entry);
    if (this.client) this.bind(channel, entry);
    return entry;
  }

  private release(channel: string): void {
    const entry = this.entries.get(channel);
    if (!entry || --entry.refs > 0) return;
    this.remove(channel); this.entries.delete(channel); this.grants.delete(channel);
    if (channel.startsWith('typing_')) {
      this.typing.delete(channel); this.typingSubjects.get(channel)?.next([]); this.lastTyping.delete(channel);
      this.typingSubjects.delete(channel);
    }
    if (channel.startsWith('status:')) {
      const id = Number(channel.slice(7));
      const states = {...this.presence$.value}; delete states[id];
      this.versions.delete(id); this.presence$.next(states);
    }
  }

  private remove(channel: string): void {
    const entry = this.entries.get(channel);
    if (entry?.subscription) {
      entry.subscription.unsubscribe();
      this.client?.removeSubscription(entry.subscription);
      entry.subscription = undefined;
    }
  }

  private bind(channel: string, entry: Entry): void {
    if (!this.client || entry.subscription) return;
    const subscription = this.client.newSubscription(channel, {getToken: async () => (await this.grant(channel, entry.resource)).token});
    entry.subscription = subscription;
    subscription.on('publication', ctx => this.zone.run(() => this.receive(channel, ctx)));
    subscription.on('subscribing', () => {
      if (channel.startsWith('status:')) this.zone.run(() => this.setPresence(Number(channel.slice(7)), 'unknown', -1));
    });
    subscription.on('subscribed', () => this.zone.run(() => {
      if (channel.startsWith('status:')) this.scheduleUserSync();
      if (channel === 'roster:online') this.synchronized$.next();
    }));
    subscription.subscribe();
  }

  private grant(channel: string, resource: Resource): Promise<RealtimeGrant> {
    if (channel === 'presence:global') {
      const cachedPresence = this.grants.get(channel);
      if (cachedPresence && cachedPresence.expiresAt * 1000 > Date.now() + 60000) return Promise.resolve(cachedPresence);
      const generation = this.generation;
      return firstValueFrom(this.http.post<any>(environment.apiUrl + '/realtime/session', {}, {headers: this.headers})).then(session => {
        if (generation !== this.generation || !this.userId || !session.enabled) throw new Error('Realtime session ended');
        this.updatePresenceGrant(session.presence);
        if (!session.presence) throw new Error('Presence is hidden');
        return session.presence;
      });
    }
    const cached = this.grants.get(channel);
    if (cached && cached.expiresAt * 1000 > Date.now() + 60000 + Math.random() * 30000) return Promise.resolve(cached);
    return new Promise((resolve, reject) => {
      const pending = this.pendingGrants.get(channel) || [];
      pending.push({resource, resolve, reject}); this.pendingGrants.set(channel, pending);
      if (!this.grantTimer) this.grantTimer = setTimeout(() => void this.flushGrants(), 40);
    });
  }

  private async flushGrants(): Promise<void> {
    this.grantTimer = undefined;
    const pending = this.pendingGrants; this.pendingGrants = new Map();
    const generation = this.generation;
    try {
      const grants = await this.fetchGrants([...pending.values()].map(values => values[0].resource));
      if (generation !== this.generation) throw new Error('Session changed');
      const returned = new Set<string>();
      for (const grant of grants) {
        returned.add(grant.channel);
        if (this.entries.has(grant.channel)) this.grants.set(grant.channel, grant);
        pending.get(grant.channel)?.forEach(request => request.resolve(grant));
      }
      pending.forEach((values, channel) => {
        if (!returned.has(channel)) values.forEach(request => request.reject(new Error('Missing channel grant')));
      });
    } catch (error) { pending.forEach(values => values.forEach(request => request.reject(error))); }
  }

  private async fetchGrants(resources: Resource[]): Promise<RealtimeGrant[]> {
    const requests: Promise<{subscriptions: RealtimeGrant[]}>[] = [];
    for (let index = 0; index < resources.length; index += 64) {
      requests.push(firstValueFrom(this.http.post<{subscriptions: RealtimeGrant[]}>(environment.apiUrl + '/realtime/subscriptions', {
        resources: resources.slice(index, index + 64),
      }, {headers: this.headers})));
    }
    return (await Promise.all(requests)).flatMap(response => response.subscriptions);
  }

  private userSyncTimer?: ReturnType<typeof setTimeout>;
  private scheduleUserSync(): void {
    if (!this.userSyncTimer) this.userSyncTimer = setTimeout(() => { this.userSyncTimer = undefined; void this.resyncUsers(); }, 60);
  }

  private async resyncUsers(): Promise<void> {
    const ids = this.watchedIds().filter(id => this.entries.get('status:' + id)?.subscription?.state === 'subscribed');
    if (!ids.length || !this.userId) return;
    const generation = this.generation;
    const epoch = this.syncEpoch;
    try {
      const response: any = await firstValueFrom(this.http.post(environment.apiUrl + '/users/presence', {ids}, {headers: this.headers}));
      if (generation !== this.generation || epoch !== this.syncEpoch) return;
      this.zone.run(() => {
        this.confirmHealth();
        ids.forEach(id => this.setPresence(id, response.statuses[id] ? 'online' : 'offline', response.version));
      });
    } catch { if (generation === this.generation && epoch === this.syncEpoch) this.zone.run(() => this.unavailable()); }
  }

  private receive(channel: string, context: any): void {
    const data = context.data;
    if (channel === 'health:presence' && data?.available === true) {
      const recovering = !this.available$.value;
      this.confirmHealth();
      if (recovering) { this.synchronized$.next(); void this.resyncUsers(); }
    } else if (channel.startsWith('status:') && typeof data?.online === 'boolean' && Number.isSafeInteger(data.version)) {
      this.setPresence(Number(channel.slice(7)), data.online ? 'online' : 'offline', data.version);
    } else if (channel === 'roster:online') this.rosterChanges$.next(data);
    else if (channel.startsWith('typing_') && typeof data?.active === 'boolean') {
      const info = context.info;
      if (!info?.user || info.user === String(this.userId) || !info.client) return;
      const actors = this.typing.get(channel) || new Map();
      const key = info.user + ':' + info.client;
      if (data.active) actors.set(key, {name: info.chanInfo?.username || info.channel_info?.username || info.connInfo?.username || info.conn_info?.username || '', until: Date.now() + 7000});
      else actors.delete(key);
      this.typing.set(channel, actors); this.emitTyping(channel);
    }
  }

  private confirmHealth(): void {
    this.available$.next(true);
    clearTimeout(this.healthTimer);
    this.healthTimer = setTimeout(() => this.zone.run(() => this.unavailable()), 25000);
  }

  private setPresence(id: number, state: PresenceState, version: number): void {
    if (version >= 0 && version < (this.versions.get(id) ?? -1)) return;
    if (version >= 0) this.versions.set(id, version); else this.versions.delete(id);
    if (this.presence$.value[id] !== state) this.presence$.next({...this.presence$.value, [id]: state});
  }

  private unavailable(): void {
    this.syncEpoch++;
    this.available$.next(false);
    const states: Record<number, PresenceState> = {};
    this.watchedIds().forEach(id => states[id] = 'unknown');
    this.presence$.next(states); this.versions.clear();
    this.typing.clear(); this.typingSubjects.forEach(subject => subject.next([]));
    this.lastTyping.clear();
  }

  typingNames(channel: string): BehaviorSubject<string[]> {
    if (!this.typingSubjects.has(channel)) this.typingSubjects.set(channel, new BehaviorSubject<string[]>([]));
    return this.typingSubjects.get(channel)!;
  }

  watchTyping(channel: string, stopOnRelease = true): () => void {
    const match = /^typing_(chat|comments):([a-zA-Z0-9-]+)$/.exec(channel || '');
    if (!match) return () => {};
    this.retain(channel, {type: match[1] as 'chat' | 'comments', id: match[2]});
    return () => {
      if (stopOnRelease) this.publishTyping(channel, false);
      this.release(channel);
    };
  }

  publishTyping(channel: string, active: boolean): void {
    const subscription = this.entries.get(channel)?.subscription;
    if (!subscription || subscription.state !== 'subscribed' || !this.available$.value) return;
    const last = this.lastTyping.get(channel);
    if (active && last && Date.now() - last < 5000) return;
    if (!active && !last) return;
    if (active) this.lastTyping.set(channel, Date.now()); else this.lastTyping.delete(channel);
    void subscription.publish({active}).catch(() => {});
  }

  private expireTyping(): void {
    this.typing.forEach((actors, channel) => {
      let changed = false;
      actors.forEach((actor, key) => { if (actor.until <= Date.now()) { actors.delete(key); changed = true; } });
      if (changed) this.zone.run(() => this.emitTyping(channel));
    });
  }

  private emitTyping(channel: string): void {
    this.typingNames(channel).next([...new Set([...this.typing.get(channel)?.values() || []].map(actor => actor.name).filter(Boolean))]);
  }

  watchRoster(): () => void {
    this.retain('roster:online', {type: 'online'});
    return () => this.release('roster:online');
  }

  snapshot(): Promise<OnlineSnapshot> {
    return firstValueFrom(this.http.get<OnlineSnapshot>(environment.apiUrl + '/users/online', {headers: this.headers}));
  }

  ngOnDestroy(): void {
    this.lifecycle.unsubscribe();
    if (isPlatformBrowser(this.platformId)) document.removeEventListener('visibilitychange', this.visibilityChanged);
    clearInterval(this.typingTimer);
    this.reset();
    this.userId = null;
  }

  private reset(): void {
    this.generation++;
    clearTimeout(this.userSyncTimer); this.userSyncTimer = undefined;
    clearTimeout(this.grantTimer); this.grantTimer = undefined;
    this.pendingGrants.forEach(values => values.forEach(request => request.reject(new Error('Session changed'))));
    this.pendingGrants.clear();
    clearTimeout(this.startTimer); this.startTimer = undefined;
    clearTimeout(this.healthTimer);
    clearTimeout(this.renewalTimer); this.sessionToken = undefined;
    this.client?.disconnect(); this.client = undefined;
    this.entries.forEach(entry => { entry.subscription = undefined; });
    this.entries.delete('presence:global'); this.entries.delete('health:presence');
    this.grants.clear(); this.unavailable();
  }
}
